import { workspaceActionTypes } from './workspace.types';

// INIT
export const workspaceInitRequest = () => ({
    type: workspaceActionTypes.WORKSPACE_INIT_REQUEST,
});
export const workspaceInitSuccess = ({ list, currentWorkspace, plan, restrictions }) => ({
    type: workspaceActionTypes.WORKSPACE_INIT_SUCCESS,
    payload: { list, currentWorkspace, plan, restrictions },
});
export const workspaceInitFailure = (error) => ({
    type: workspaceActionTypes.WORKSPACE_INIT_FAILURE,
    payload: error,
});

// SWITCH WORKSPACE
export const workspaceSwitchRequest = (workspaceId) => ({
    type: workspaceActionTypes.WORKSPACE_SWITCH_REQUEST,
    payload: workspaceId,
});
export const workspaceSwitchSuccess = ({ currentWorkspace, plan, restrictions }) => ({
    type: workspaceActionTypes.WORKSPACE_SWITCH_SUCCESS,
    payload: { currentWorkspace, plan, restrictions },
});
export const workspaceSwitchFailure = (error) => ({
    type: workspaceActionTypes.WORKSPACE_SWITCH_FAILURE,
    payload: error,
});

// FETCH TEAMS
export const workspaceFetchTeamsRequest = (workspaceId) => ({
    type: workspaceActionTypes.WORKSPACE_FETCH_TEAMS_REQUEST,
    payload: workspaceId,
});
export const workspaceFetchTeamsSuccess = (teams) => ({
    type: workspaceActionTypes.WORKSPACE_FETCH_TEAMS_SUCCESS,
    payload: teams,
});
export const workspaceFetchTeamsFailure = (error) => ({
    type: workspaceActionTypes.WORKSPACE_FETCH_TEAMS_FAILURE,
    payload: error,
});

// FETCH MEMBERS
export const workspaceFetchMembersRequest = (workspaceId) => ({
    type: workspaceActionTypes.WORKSPACE_FETCH_MEMBERS_REQUEST,
    payload: workspaceId,
});
export const workspaceFetchMembersSuccess = (members) => ({
    type: workspaceActionTypes.WORKSPACE_FETCH_MEMBERS_SUCCESS,
    payload: members,
});
export const workspaceFetchMembersFailure = (error) => ({
    type: workspaceActionTypes.WORKSPACE_FETCH_MEMBERS_FAILURE,
    payload: error,
});

// RESET
export const workspaceReset = () => ({
    type: workspaceActionTypes.WORKSPACE_RESET,
});

// CREATE WORKSPACE — callback-style; the saga invokes onSuccess / onError.
export const workspaceCreateRequest = ({ title, email, onSuccess, onError }) => ({
    type: workspaceActionTypes.WORKSPACE_CREATE_REQUEST,
    payload: { title, email, onSuccess, onError },
});
export const workspaceCreateSuccess = (workspace) => ({
    type: workspaceActionTypes.WORKSPACE_CREATE_SUCCESS,
    payload: workspace,
});
export const workspaceCreateFailure = (error) => ({
    type: workspaceActionTypes.WORKSPACE_CREATE_FAILURE,
    payload: error,
});

// INVITE MEMBER (used by the create-workspace chain; modal flow lives in redux/main)
export const workspaceInviteMemberRequest = ({ workspaceId, email }) => ({
    type: workspaceActionTypes.WORKSPACE_INVITE_MEMBER_REQUEST,
    payload: { workspaceId, email },
});
export const workspaceInviteMemberSuccess = ({ workspaceId, email, inviteId }) => ({
    type: workspaceActionTypes.WORKSPACE_INVITE_MEMBER_SUCCESS,
    payload: { workspaceId, email, inviteId },
});
export const workspaceInviteMemberFailure = (error) => ({
    type: workspaceActionTypes.WORKSPACE_INVITE_MEMBER_FAILURE,
    payload: error,
});

// UPDATE WORKSPACE — callback-style so the UI can revert on failure.
export const workspaceUpdateRequest = ({ id, fields, onSuccess, onError }) => ({
    type: workspaceActionTypes.WORKSPACE_UPDATE_REQUEST,
    payload: { id, fields, onSuccess, onError },
});
export const workspaceUpdateSuccess = ({ id, fields }) => ({
    type: workspaceActionTypes.WORKSPACE_UPDATE_SUCCESS,
    payload: { id, fields },
});
export const workspaceUpdateFailure = (error) => ({
    type: workspaceActionTypes.WORKSPACE_UPDATE_FAILURE,
    payload: error,
});

// LEAVE WORKSPACE — callback-style so the modal can close / report failure.
export const workspaceLeaveRequest = ({ id, onSuccess, onError }) => ({
    type: workspaceActionTypes.WORKSPACE_LEAVE_REQUEST,
    payload: { id, onSuccess, onError },
});
export const workspaceLeaveSuccess = (id) => ({
    type: workspaceActionTypes.WORKSPACE_LEAVE_SUCCESS,
    payload: id,
});
export const workspaceLeaveFailure = (error) => ({
    type: workspaceActionTypes.WORKSPACE_LEAVE_FAILURE,
    payload: error,
});

// ARCHIVE WORKSPACE — owner-only; backs the "delete workspace" UX (backend
// schedules permanent deletion from the archived state). Callback-style.
export const workspaceArchiveRequest = ({ id, onSuccess, onError }) => ({
    type: workspaceActionTypes.WORKSPACE_ARCHIVE_REQUEST,
    payload: { id, onSuccess, onError },
});
export const workspaceArchiveSuccess = (id) => ({
    type: workspaceActionTypes.WORKSPACE_ARCHIVE_SUCCESS,
    payload: id,
});
export const workspaceArchiveFailure = (error) => ({
    type: workspaceActionTypes.WORKSPACE_ARCHIVE_FAILURE,
    payload: error,
});

// REMOVE MEMBER — owner/admin removes another member. Callback-style.
// `userId` is the target's users.id (DELETE …/members/:userId — see the
// members-management integration doc), NOT the membership-row id.
export const workspaceRemoveMemberRequest = ({ workspaceId, userId, onSuccess, onError }) => ({
    type: workspaceActionTypes.WORKSPACE_REMOVE_MEMBER_REQUEST,
    payload: { workspaceId, userId, onSuccess, onError },
});
export const workspaceRemoveMemberSuccess = (userId) => ({
    type: workspaceActionTypes.WORKSPACE_REMOVE_MEMBER_SUCCESS,
    payload: userId,
});
export const workspaceRemoveMemberFailure = (error) => ({
    type: workspaceActionTypes.WORKSPACE_REMOVE_MEMBER_FAILURE,
    payload: error,
});

// FETCH INVITES — pending workspace invites (owner/admin only).
export const workspaceFetchInvitesRequest = (workspaceId) => ({
    type: workspaceActionTypes.WORKSPACE_FETCH_INVITES_REQUEST,
    payload: workspaceId,
});
export const workspaceFetchInvitesSuccess = (invites) => ({
    type: workspaceActionTypes.WORKSPACE_FETCH_INVITES_SUCCESS,
    payload: invites,
});
export const workspaceFetchInvitesFailure = (error) => ({
    type: workspaceActionTypes.WORKSPACE_FETCH_INVITES_FAILURE,
    payload: error,
});

// REVOKE INVITE — owner/admin revokes a pending invite. Callback-style so the
// UI can revert an optimistic drop on failure.
export const workspaceRevokeInviteRequest = ({ workspaceId, inviteId, onSuccess, onError }) => ({
    type: workspaceActionTypes.WORKSPACE_REVOKE_INVITE_REQUEST,
    payload: { workspaceId, inviteId, onSuccess, onError },
});
export const workspaceRevokeInviteSuccess = (inviteId) => ({
    type: workspaceActionTypes.WORKSPACE_REVOKE_INVITE_SUCCESS,
    payload: inviteId,
});
export const workspaceRevokeInviteFailure = (error) => ({
    type: workspaceActionTypes.WORKSPACE_REVOKE_INVITE_FAILURE,
    payload: error,
});
